/**
 * Affiliates Service
 * Handles all affiliate-related API operations
 */

import { supabase } from "./supabase";
import type {
  PaginatedResponse,
  PaginationParams,
  UUID,
} from "../types/common.types";
import type {
  Affiliate,
  AffiliateWithPartner,
  AffiliateFormData,
  AffiliateFilters,
} from "../types/partner.types";

// =============================================================================
// Affiliate CRUD
// =============================================================================

/**
 * Get affiliates for a partner with pagination
 */
export const getAffiliates = async (
  partnerId: UUID,
  params?: PaginationParams,
  filters?: AffiliateFilters,
): Promise<PaginatedResponse<Affiliate>> => {
  const {
    page = 1,
    limit = 25,
    sortBy = "name",
    sortOrder = "asc",
  } = params || {};
  const start = (page - 1) * limit;
  const end = start + limit - 1;

  let query = supabase
    .from("affiliates")
    .select("*", { count: "exact" })
    .eq("partner_id", partnerId)
    .order(sortBy, { ascending: sortOrder === "asc" })
    .range(start, end);

  // Apply filters
  if (filters?.search) {
    query = query.or(
      `name.ilike.*${filters.search}*,affiliate_number.ilike.*${filters.search}*`,
    );
  }

  if (filters?.is_active !== undefined) {
    query = query.eq("is_active", filters.is_active);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  return {
    data: data || [],
    total: count || 0,
    page,
    limit,
    totalPages: Math.ceil((count || 0) / limit),
  };
};

/**
 * Get all affiliates across all partners with pagination
 */
export const getAllAffiliates = async (
  params?: PaginationParams,
  filters?: AffiliateFilters,
): Promise<PaginatedResponse<AffiliateWithPartner>> => {
  const {
    page = 1,
    limit = 25,
    sortBy = "name",
    sortOrder = "asc",
  } = params || {};
  const start = (page - 1) * limit;
  const end = start + limit - 1;

  let query = supabase
    .from("affiliates")
    .select(
      `
      *,
      partner:partners!inner (
        id,
        name,
        is_active,
        is_deleted
      )
    `,
      { count: "exact" },
    )
    .eq("partner.is_deleted", false)
    .order(sortBy, { ascending: sortOrder === "asc" })
    .range(start, end);

  // Apply filters
  if (filters?.search) {
    query = query.or(
      `name.ilike.*${filters.search}*,affiliate_number.ilike.*${filters.search}*`,
    );
  }

  if (filters?.partner_id) {
    query = query.eq("partner_id", filters.partner_id);
  }

  if (filters?.is_active !== undefined) {
    query = query.eq("is_active", filters.is_active);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  return {
    data: data || [],
    total: count || 0,
    page,
    limit,
    totalPages: Math.ceil((count || 0) / limit),
  };
};

/**
 * Get a single affiliate by ID
 */
export const getAffiliate = async (id: UUID): Promise<AffiliateWithPartner> => {
  const { data, error } = await supabase
    .from("affiliates")
    .select(
      `
      *,
      partner:partners (
        id,
        name,
        is_active,
        is_deleted
      )
    `,
    )
    .eq("id", id)
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Get affiliate by affiliate number
 */
export const getAffiliateByNumber = async (
  affiliateNumber: string,
): Promise<AffiliateWithPartner | null> => {
  const { data, error } = await supabase
    .from("affiliates")
    .select(
      `
      *,
      partner:partners (
        id,
        name,
        is_active,
        is_deleted
      )
    `,
    )
    .eq("affiliate_number", affiliateNumber.toUpperCase())
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    } // Not found
    throw error;
  }
  return data;
};

/**
 * Create a new affiliate
 */
export const createAffiliate = async (
  affiliate: AffiliateFormData,
): Promise<Affiliate> => {
  const { data, error } = await supabase
    .from("affiliates")
    .insert({
      ...affiliate,
      affiliate_number: affiliate.affiliate_number?.toUpperCase() || null, // Will be auto-generated if null
      is_active: affiliate.is_active ?? true,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Update an affiliate
 */
export const updateAffiliate = async (
  id: UUID,
  updates: Partial<AffiliateFormData>,
): Promise<Affiliate> => {
  // Don't allow changing affiliate_number after creation
  const { affiliate_number, ...safeUpdates } = updates;

  const { data, error } = await supabase
    .from("affiliates")
    .update({
      ...safeUpdates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Delete an affiliate (hard delete - not soft)
 */
export const deleteAffiliate = async (id: UUID): Promise<void> => {
  const { error } = await supabase.from("affiliates").delete().eq("id", id);

  if (error) {
    throw error;
  }
};

/**
 * Toggle affiliate active status
 */
export const toggleAffiliateActive = async (
  id: UUID,
  isActive: boolean,
): Promise<Affiliate> => {
  const { data, error } = await supabase
    .from("affiliates")
    .update({
      is_active: isActive,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Bulk update affiliate status
 */
export const bulkUpdateAffiliateStatus = async (
  ids: UUID[],
  isActive: boolean,
): Promise<void> => {
  const { error } = await supabase
    .from("affiliates")
    .update({
      is_active: isActive,
      updated_at: new Date().toISOString(),
    })
    .in("id", ids);

  if (error) {
    throw error;
  }
};

// =============================================================================
// Dropdown Options
// =============================================================================

/**
 * Get affiliates for dropdown selection (optionally filtered by partner)
 */
export const getAffiliatesForDropdown = async (
  partnerId?: UUID,
): Promise<Array<{ value: UUID; label: string; affiliateNumber: string }>> => {
  let query = supabase
    .from("affiliates")
    .select("id, name, affiliate_number")
    .eq("is_active", true)
    .order("name");

  if (partnerId) {
    query = query.eq("partner_id", partnerId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data || []).map((a) => ({
    value: a.id,
    label: `${a.name} (${a.affiliate_number})`,
    affiliateNumber: a.affiliate_number,
  }));
};

// =============================================================================
// Validation
// =============================================================================

/**
 * Check if affiliate number is available
 */
export const isAffiliateNumberAvailable = async (
  affiliateNumber: string,
  excludeId?: UUID,
): Promise<boolean> => {
  let query = supabase
    .from("affiliates")
    .select("id")
    .eq("affiliate_number", affiliateNumber.toUpperCase());

  if (excludeId) {
    query = query.neq("id", excludeId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }
  return (data || []).length === 0;
};
